/**
 * Lecture d'une liste officielle de résultats, dans le navigateur.
 *
 * Le fichier ne quitte jamais le poste : il est lu ici, découpé en lignes et
 * en champs, et seules ces lignes partent au serveur pour le rapprochement.
 * Rien n'est déposé sur le disque du serveur - c'était le piège des fichiers
 * téléversés depuis un poste local, qui restaient sur ce poste.
 *
 *   Excel (.xlsx, .xls), ODS, CSV : par SheetJS (`xlsx`), cellule par cellule.
 *   PDF : par pdf.js, chargé à la demande (un seul import, pas pour tout le
 *         monde). Les morceaux de texte sont regroupés en lignes par leur
 *         hauteur sur la page, puis en colonnes d'après la ligne de titres.
 *         Un PDF scanné (une image) n'a pas de texte : il est refusé avec un
 *         message qui dit quoi demander à la place.
 */
import * as XLSX from 'xlsx';

export const FORMATS_ACCEPTES = '.xlsx,.xls,.ods,.csv,.pdf';

/** Les champs qu'une colonne peut porter, dans l'ordre où l'écran les propose. */
export const CHAMPS = [
  { cle: 'nom_complet', libelle: 'Nom et prénoms (ensemble)' },
  { cle: 'nom', libelle: 'Nom' },
  { cle: 'prenom', libelle: 'Prénom(s)' },
  { cle: 'matricule_national', libelle: 'Matricule national' },
  { cle: 'numero_table', libelle: 'Numéro de table' },
  { cle: 'date_naissance', libelle: 'Date de naissance' },
  { cle: 'resultat', libelle: 'Résultat' },
  { cle: 'mention', libelle: 'Mention' },
  // Ne servent qu'à déclarer candidat libre un élève de la liste absent de KLASYS.
  { cle: 'sexe', libelle: 'Genre' },
  { cle: 'lieu_naissance', libelle: 'Lieu de naissance' }
];

const normaliser = (texte) => String(texte ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, ' ')
  .trim();

// Les titres de colonnes reconnus. « Nom et prénoms » avant « Nom » : le
// premier contient le second.
const MOTIFS_ENTETE = [
  ['nom_complet', /^(NOMS? (ET|E) PRENOMS?|NOMS? PRENOMS?|NOM COMPLET|IDENTITE|NOMS? DU CANDIDAT|CANDIDATS?)$/],
  ['prenom', /^PRENOMS?( DU CANDIDAT)?$/],
  ['nom', /^(NOMS?|NOM DE FAMILLE|NOMS? DU CANDIDAT)$/],
  ['matricule_national', /(MATRICULE|^MATR|^N MLE|^MLE$|IDENTIFIANT|^N CANDIDAT|CODE CANDIDAT|^INE$)/],
  ['numero_table', /(^N TABLE|NUMERO DE TABLE|NUM TABLE|^TABLE$|^N DE TABLE)/],
  ['date_naissance', /(NAISSANCE|^NE E? LE|^NE LE|^DATE NAISS|^DN$)/],
  ['resultat', /(RESULTAT|DECISION|^STATUT|OBSERVATION|^ADMISSION)/],
  ['mention', /(MENTION|APPRECIATION)/],
  ['sexe', /^(GENRE|SEXE|SEX|G|S)$/]
];

/**
 * Le champ qu'annonce un titre de colonne, ou null.
 *
 * Les marques de pluriel et de féminin entre parenthèses sont résolues avant
 * la comparaison : « Nom(s) » se lit « Noms », « Prénom(s) » « Prénoms »,
 * « Né(e) le » « Né le ». Sans cela, la liste officielle du CEPE 2026
 * (SIGCEPE) n'avait ni colonne de nom ni colonne de prénom, et aucune de ses
 * lignes ne pouvait être rapprochée.
 */
export const champDuTitre = (titre) => {
  const resolu = String(titre ?? '')
    .replace(/\(\s*e?s\s*\)/gi, 's')
    .replace(/\(\s*e\s*\)/gi, '');
  const t = normaliser(resolu);
  if (!t) return null;
  // « Lieu de naissance » parle de naissance, mais ce n'est pas une date.
  if (/^LIEU/.test(t)) return /NAISS/.test(t) ? 'lieu_naissance' : null;
  const trouve = MOTIFS_ENTETE.find(([, motif]) => motif.test(t));
  return trouve ? trouve[0] : null;
};

const champsDeLaLigne = (cellules) => {
  const correspondance = {};
  cellules.forEach((cellule, colonne) => {
    const champ = champDuTitre(cellule);
    if (champ && correspondance[champ] === undefined) correspondance[champ] = colonne;
  });
  return correspondance;
};

const estLigneDeTitres = (cellules) => {
  const champs = Object.keys(champsDeLaLigne(cellules));
  const porteUnNom = champs.some((c) => ['nom_complet', 'nom', 'prenom', 'matricule_national'].includes(c));
  return champs.length >= 2 && porteUnNom;
};

/**
 * La ligne de titres parmi les trente premières, et la colonne de chaque
 * champ reconnu. `indexEntete` vaut -1 quand aucune ligne n'en a l'air.
 */
export const detecterEntetes = (lignes) => {
  const limite = Math.min(lignes.length, 30);
  for (let i = 0; i < limite; i += 1) {
    if (estLigneDeTitres(lignes[i])) {
      return { indexEntete: i, correspondance: champsDeLaLigne(lignes[i]) };
    }
  }
  return { indexEntete: -1, correspondance: {} };
};

/**
 * Les lignes à envoyer au serveur.
 *
 * Avec des colonnes : un objet par ligne de données, champs lus dans les
 * colonnes choisies ; les lignes vides et les titres répétés (en haut de
 * chaque page d'un PDF) sont écartés. Sans colonnes (`texte`) : la ligne
 * entière, que le serveur compare en bloc aux noms de nos candidats.
 */
export const construireLignes = (lignes, indexEntete, correspondance, { enBloc = false } = {}) => {
  const resultat = [];
  lignes.forEach((cellules, i) => {
    if (i <= indexEntete) return;
    const remplies = cellules.map((c) => String(c ?? '').trim());
    if (!remplies.some(Boolean)) return;
    if (estLigneDeTitres(remplies)) return;

    if (enBloc) {
      resultat.push({ ligne: i + 1, texte: remplies.filter(Boolean).join(' ') });
      return;
    }

    const ligne = { ligne: i + 1 };
    for (const { cle } of CHAMPS) {
      const colonne = correspondance[cle];
      if (colonne !== undefined && colonne !== null && remplies[colonne]) ligne[cle] = remplies[colonne];
    }
    if (Object.keys(ligne).length > 1) resultat.push(ligne);
  });
  return resultat;
};

// ============================================================================
// Élèves ou texte du document, et contrôle de lecture
// ============================================================================

/** Les mots d'un nom : deux lettres au moins, jamais un nombre (comme le serveur). */
const motsDuNom = (texte) => normaliser(texte).split(' ').filter((m) => m.length >= 2 && !/\d/.test(m));

const MOTIF_DATE = /\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\b|\b(\d{4})-(\d{2})-(\d{2})\b/g;

/** Les dates plausibles écrites dans un texte (JJ/MM/AAAA ou AAAA-MM-JJ). */
const datesDans = (texte) => {
  const dates = [];
  for (const m of String(texte || '').matchAll(MOTIF_DATE)) {
    const [jour, mois] = m[1] ? [Number(m[1]), Number(m[2])] : [Number(m[6]), Number(m[5])];
    if (mois >= 1 && mois <= 12 && jour >= 1 && jour <= 31) dates.push(m[0]);
  }
  return dates;
};

/** Un identifiant : un ou deux morceaux, au moins un chiffre, aucun vrai mot. */
const ressembleIdentifiant = (valeur) => {
  const brut = String(valeur || '').trim();
  if (!brut || brut.split(/\s+/).length > 2) return false;
  const compact = brut.replace(/\s+/g, '');
  return compact.length >= 4 && compact.length <= 24 && /\d/.test(compact) && /^[A-Za-z0-9./-]+$/.test(compact);
};

/**
 * Sépare les lignes d'élèves du texte du document (en-têtes, titres, pied de
 * page) avant le rapprochement.
 *
 * Une ligne d'élève porte un nom ET de quoi l'identifier : une date de
 * naissance, ou un matricule / numéro de table qui en a la forme. Sur la
 * liste du CEPE 2026, « MINISTERE DE L'ENSEIGNEMENT », « Session : JUIN 2026 »
 * ou « SIGCEPE © 2022 » partaient sinon parmi les élèves « hors école », et
 * noyaient les vrais cas à regarder. Une liste sans colonne de date ni de
 * matricule garde toute ligne d'au moins deux mots.
 *
 * Retourne `{ eleves, texte }`.
 */
export const classerLignes = (lignes, { enBloc = false, correspondance = {} } = {}) => {
  const identifiable = enBloc || ['date_naissance', 'matricule_national', 'numero_table']
    .some((c) => correspondance[c] !== undefined);
  const eleves = [];
  const texte = [];
  for (const ligne of lignes) {
    const nom = ligne.texte || ligne.nom_complet || [ligne.nom, ligne.prenom].filter(Boolean).join(' ');
    const mots = motsDuNom(nom);
    const aDate = datesDans(enBloc ? ligne.texte : ligne.date_naissance).length > 0
      || /^\d{5}$/.test(String(ligne.date_naissance || '').trim());
    const aIdentifiant = ressembleIdentifiant(ligne.matricule_national) || ressembleIdentifiant(ligne.numero_table);

    let eleve;
    if (enBloc) eleve = mots.length >= 2 && aDate;
    else if (identifiable) eleve = mots.length >= 1 && (aDate || aIdentifiant);
    else eleve = mots.length >= 2;
    (eleve ? eleves : texte).push(ligne);
  }
  return { eleves, texte };
};

/** Majuscules, lettres et chiffres seulement : « eadud 260 045* » -> « EADUD260045 ». */
const compacte = (valeur) => String(valeur || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** La forme d'un identifiant : « EADUD260001 » -> « AAAAA999999 ». */
const formeIdentifiant = (valeur) => String(valeur || '')
  .replace(/\s+/g, '')
  .toUpperCase()
  .replace(/[A-Z]/g, 'A')
  .replace(/\d/g, '9');

/**
 * A-t-on lu toutes les lignes d'élèves du document ?
 *
 * Une autre méthode que la lecture elle-même, sinon elle ne prouverait rien :
 *
 *   par le matricule  les matricules lus ont presque tous la même forme
 *                     (EADUD260001 -> AAAAA999999). On compte, dans le TEXTE
 *                     BRUT du fichier, tous les mots de cette forme : s'il y
 *                     en a autant que d'élèves lus, aucune ligne n'est perdue.
 *   par la date       sans matricule : une date de naissance par élève lu.
 *                     Deux dates sur une ligne trahissent deux élèves fondus.
 *
 * Retourne `{ methode, ok, document, lus, exemple }`, ou `{ methode: null }`
 * quand la liste n'offre ni matricule ni date.
 */
export const controleLecture = ({ eleves, texteBrut = [] }) => {
  const formes = new Map();
  for (const l of eleves) {
    if (!ressembleIdentifiant(l.matricule_national)) continue;
    const forme = formeIdentifiant(l.matricule_national);
    const entree = formes.get(forme) || { nombre: 0, exemple: l.matricule_national };
    entree.nombre += 1;
    formes.set(forme, entree);
  }
  const [dominante] = [...formes.entries()].sort((a, b) => b[1].nombre - a[1].nombre);
  if (dominante && dominante[1].nombre >= Math.max(3, eleves.length / 2)) {
    const [forme, { nombre, exemple }] = dominante;

    // Les matricules lus, et les élèves lus dont le matricule n'a pas cette
    // forme (vide, débordé dans le nom, écrit avec un espace en trop...).
    const lus = new Map();
    const sansForme = [];
    for (const l of eleves) {
      if (ressembleIdentifiant(l.matricule_national) && formeIdentifiant(l.matricule_national) === forme) {
        const m = compacte(l.matricule_national);
        lus.set(m, (lus.get(m) || 0) + 1);
      } else {
        sansForme.push(l);
      }
    }

    // Les matricules du texte brut, avec la ligne du document où chacun se trouve.
    let document = 0;
    const dansDocument = new Map();
    for (const ligne of texteBrut) {
      for (const morceau of String(ligne).split(/\s+/)) {
        const propre = morceau.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
        if (!propre || formeIdentifiant(propre) !== forme) continue;
        document += 1;
        const m = compacte(propre);
        const entree = dansDocument.get(m) || { nombre: 0, texte: String(ligne).trim() };
        entree.nombre += 1;
        dansDocument.set(m, entree);
      }
    }

    // Ce qui ne se recoupe pas, désigné par son nom plutôt que compté : la
    // personne voit tout de suite les lignes en cause, sans les chercher dans
    // les grandes listes (demande de Daniel du 01/11/2026).
    const manquants = [];
    for (const [matricule, { nombre: fois, texte }] of dansDocument) {
      for (let i = lus.get(matricule) || 0; i < fois; i += 1) manquants.push({ matricule, texte });
    }
    const doublons = [...lus.entries()]
      .filter(([matricule, fois]) => fois > (dansDocument.get(matricule)?.nombre || 0))
      .map(([matricule]) => matricule);

    // Un matricule du document absent des matricules lus, mais présent dans
    // une autre case d'un élève lu sans matricule de cette forme : la ligne a
    // bien été lue, seule l'écriture du matricule diffère. Rien n'est perdu.
    const restants = [...sansForme];
    for (const manquant of manquants) {
      const i = restants.findIndex((l) => compacte(Object.values(l).join(' ')).includes(manquant.matricule));
      if (i >= 0) {
        manquant.eleve = restants[i];
        restants.splice(i, 1);
      }
    }
    const nonExpliques = manquants.filter((m) => !m.eleve);

    return {
      methode: 'matricule',
      ok: nonExpliques.length === 0 && doublons.length === 0,
      document,
      lus: nombre,
      eleves: eleves.length,
      exemple,
      // Lus quand même, avec une autre écriture du matricule.
      ecritsAutrement: manquants.filter((m) => m.eleve).map((m) => ({ matricule: m.matricule, ligne: m.eleve })),
      // Dans le document, retrouvés chez aucun élève lu : les lignes à vérifier.
      manquants: nonExpliques.slice(0, 50).map((m) => ({ matricule: m.matricule, texte: m.texte })),
      // Lus deux fois, alors que le document ne les porte qu'une fois.
      doublons: doublons.slice(0, 50),
      // Élèves lus sans matricule de cette forme, qu'aucun manquant n'explique.
      sansForme: restants.slice(0, 50)
    };
  }

  const avecDate = eleves.filter((l) => l.date_naissance || l.texte);
  if (avecDate.length > 0) {
    const nombreDates = (l) => datesDans(l.texte || l.date_naissance).length
      + (/^\d{5}$/.test(String(l.date_naissance || '').trim()) ? 1 : 0);
    const dates = eleves.reduce((n, l) => n + nombreDates(l), 0);
    return {
      methode: 'date',
      ok: dates === eleves.length,
      document: dates,
      lus: eleves.length,
      eleves: eleves.length,
      // Les lignes sans date, ou avec plusieurs (deux élèves fondus).
      anomalies: eleves.filter((l) => nombreDates(l) !== 1).slice(0, 50)
        .map((l) => ({ ligne: l, dates: nombreDates(l) }))
    };
  }
  return { methode: null };
};

/** Le nom de colonne façon tableur : A, B, ..., Z, AA. */
export const lettreColonne = (index) => XLSX.utils.encode_col(index);

// ============================================================================
// Tableurs
// ============================================================================

const pad = (n) => String(n).padStart(2, '0');

/**
 * Le texte d'une cellule, lu à la main plutôt que par sheet_to_json : celui-ci
 * rend un long matricule en « 1.23457E+11 » et une date au format par défaut
 * d'Excel en « 3/12/08 », à l'américaine, que la suite lirait 3 décembre.
 */
const texteCellule = (cellule) => {
  if (!cellule) return '';
  if (cellule.t === 'n' && cellule.z && XLSX.SSF.is_date(cellule.z)) {
    const d = XLSX.SSF.parse_date_code(cellule.v);
    return d ? `${d.y}-${pad(d.m)}-${pad(d.d)}` : '';
  }
  if (cellule.t === 'n' && Number.isInteger(cellule.v)) return String(cellule.v);
  if (cellule.t === 'd' && cellule.v instanceof Date) return cellule.v.toISOString().slice(0, 10);
  return String(cellule.w ?? cellule.v ?? '');
};

const lignesDeLaFeuille = (feuille) => {
  if (!feuille || !feuille['!ref']) return [];
  const plage = XLSX.utils.decode_range(feuille['!ref']);
  const lignes = [];
  for (let r = plage.s.r; r <= plage.e.r; r += 1) {
    const ligne = [];
    for (let c = plage.s.c; c <= plage.e.c; c += 1) {
      ligne.push(texteCellule(feuille[XLSX.utils.encode_cell({ r, c })]).trim());
    }
    lignes.push(ligne);
  }
  // Les lignes vides de fin de feuille n'ont rien à dire.
  while (lignes.length && !lignes[lignes.length - 1].some(Boolean)) lignes.pop();
  return lignes;
};

export const lireClasseur = (donnees, type) => {
  const classeur = XLSX.read(donnees, { type, cellNF: true });
  return classeur.SheetNames.map((nom) => ({ nom, lignes: lignesDeLaFeuille(classeur.Sheets[nom]) }));
};

// ============================================================================
// PDF
// ============================================================================

/**
 * Regroupe les morceaux de texte d'un PDF en lignes, puis en cellules.
 *
 * `pages` : pour chaque page, les morceaux `{ texte, x, y, largeur, hauteur }`
 * (y croissant vers le haut, comme dans un PDF). Deux morceaux sont sur la
 * même ligne si leurs hauteurs diffèrent de moins de la moitié d'un
 * caractère ; dans une ligne, un blanc plus large qu'un caractère ouvre une
 * nouvelle cellule.
 *
 * Retourne, page après page, des lignes de cellules `{ x, texte }`.
 */
export const regrouperTextePdf = (pages) => {
  const lignes = [];
  for (const morceaux of pages) {
    const utiles = morceaux
      .filter((m) => String(m.texte || '').trim())
      .sort((a, b) => (b.y - a.y) || (a.x - b.x));

    const parLigne = [];
    for (const m of utiles) {
      const tolerance = Math.max(2, (m.hauteur || 10) * 0.5);
      const courante = parLigne[parLigne.length - 1];
      if (courante && Math.abs(courante.y - m.y) <= tolerance) courante.morceaux.push(m);
      else parLigne.push({ y: m.y, morceaux: [m] });
    }

    for (const { morceaux: rang } of parLigne) {
      rang.sort((a, b) => a.x - b.x);
      const cellules = [];
      for (const m of rang) {
        const texte = String(m.texte);
        const largeurCaractere = (m.largeur || texte.length * 5) / Math.max(1, texte.length);
        const derniere = cellules[cellules.length - 1];
        if (derniere && m.x - derniere.fin <= Math.max(3, largeurCaractere * 1.2)) {
          derniere.texte += (m.x - derniere.fin > largeurCaractere * 0.25 ? ' ' : '') + texte;
          derniere.fin = m.x + (m.largeur || 0);
        } else {
          cellules.push({ x: m.x, fin: m.x + (m.largeur || 0), texte });
        }
      }
      lignes.push(cellules.map(({ x, fin, texte }) => ({ x, fin, texte: texte.replace(/\s+/g, ' ').trim() })));
    }
  }
  return lignes;
};

/**
 * Range les cellules de chaque ligne sous les colonnes de la ligne de titres.
 *
 * Les titres d'un tableau sont souvent CENTRÉS dans leur colonne, et les
 * données ALIGNÉES À GAUCHE : dans la liste du CEPE 2026, « Nom(s) » commence
 * à x=154 et « BACKALA ELENGA » à x=102. Ranger une cellule sous le dernier
 * titre qui commence avant elle envoyait donc le nom de famille dans la
 * colonne Matricule et les prénoms dans la colonne Nom.
 *
 * Chaque colonne occupe désormais la bande comprise entre les milieux de ses
 * voisins (milieu des titres) ; une cellule va dans la bande qu'elle recouvre
 * le plus. Centrés, à gauche ou à droite, titres et données retombent dans la
 * même bande. Sans ligne de titres, chaque ligne reste en un seul bloc.
 *
 * Retourne `{ lignes: string[][], enBloc }`.
 */
export const alignerColonnesPdf = (lignesCellules) => {
  const iTitres = lignesCellules.findIndex((cellules) => estLigneDeTitres(cellules.map((c) => c.texte)));
  if (iTitres < 0) {
    return {
      enBloc: true,
      lignes: lignesCellules.map((cellules) => [cellules.map((c) => c.texte).join(' ')])
    };
  }

  const milieux = lignesCellules[iTitres].map((c) => (c.x + (c.fin ?? c.x)) / 2);
  const bornes = milieux.slice(0, -1).map((m, j) => (m + milieux[j + 1]) / 2);
  const bandes = milieux.map((_, j) => [j === 0 ? -Infinity : bornes[j - 1], j === milieux.length - 1 ? Infinity : bornes[j]]);

  const colonneDe = (cellule) => {
    const debut = cellule.x;
    const fin = Math.max(cellule.fin ?? cellule.x, debut);
    let meilleure = 0;
    let recouvrement = -1;
    bandes.forEach(([gauche, droite], j) => {
      const r = Math.min(fin, droite) - Math.max(debut, gauche);
      if (r > recouvrement) {
        recouvrement = r;
        meilleure = j;
      }
    });
    // Cellule sans largeur connue : la bande qui contient son début.
    if (recouvrement <= 0) {
      const j = bandes.findIndex(([gauche, droite]) => debut >= gauche && debut < droite);
      if (j >= 0) meilleure = j;
    }
    return meilleure;
  };

  const lignes = lignesCellules.slice(iTitres).map((cellules, rang) => {
    if (rang === 0) return cellules.map((c) => c.texte);
    const ligne = milieux.map(() => '');
    for (const c of cellules) {
      const colonne = colonneDe(c);
      ligne[colonne] = ligne[colonne] ? `${ligne[colonne]} ${c.texte}` : c.texte;
    }
    return ligne;
  });
  return { enBloc: false, lignes };
};

let pdfjsCharge = null;

/**
 * pdf.js, chargé au premier PDF seulement. Le « worker » tourne dans la page
 * (pdf.worker.entry le pose sur window) : rien à servir à part, et une liste
 * de quelques pages se lit en une ou deux secondes.
 */
const chargerPdfJs = async () => {
  if (!pdfjsCharge) {
    pdfjsCharge = (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
      await import('pdfjs-dist/legacy/build/pdf.worker.entry');
      return pdfjs;
    })();
  }
  return pdfjsCharge;
};

export const lirePdf = async (donnees) => {
  const pdfjs = await chargerPdfJs();
  // verbosity 0 : seules les erreurs. Les polices standard manquantes ne
  // gênent que l'affichage, pas la lecture du texte - inutile d'en avertir.
  const document = await pdfjs.getDocument({ data: donnees, verbosity: 0 }).promise;
  const pages = [];
  for (let n = 1; n <= document.numPages; n += 1) {
    const page = await document.getPage(n);
    const contenu = await page.getTextContent();
    pages.push(contenu.items.map((item) => ({
      texte: item.str,
      x: item.transform[4],
      y: item.transform[5],
      largeur: item.width,
      hauteur: item.height || Math.abs(item.transform[3]) || 10
    })));
  }

  const morceaux = pages.reduce((n, p) => n + p.filter((m) => String(m.texte || '').trim()).length, 0);
  if (morceaux === 0) {
    throw new Error(
      "Ce PDF ne contient pas de texte : c'est une image scannée, que KLASYS ne sait pas lire. "
        + 'Demandez la liste en Excel, ou un PDF exporté depuis un ordinateur (pas scanné).'
    );
  }
  const regroupees = regrouperTextePdf(pages);
  // Le texte brut, ligne visuelle par ligne visuelle, pour le contrôle de
  // lecture : il ne dépend pas du découpage en colonnes.
  return {
    ...alignerColonnesPdf(regroupees),
    texteBrut: regroupees.map((cellules) => cellules.map((c) => c.texte).join(' '))
  };
};

// ============================================================================
// Point d'entrée
// ============================================================================

export const formatDuFichier = (nom) => {
  const m = String(nom || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
};

/**
 * Lit le fichier choisi.
 *
 * Retourne `{ format, feuilles: [{ nom, lignes: string[][], enBloc, texteBrut }] }`.
 */
export const lireFichier = async (fichier) => {
  const format = formatDuFichier(fichier.name);
  const enTableur = (feuilles) => feuilles.map((f) => ({
    ...f,
    enBloc: false,
    texteBrut: f.lignes.map((cellules) => cellules.join(' '))
  }));

  if (format === 'pdf') {
    const { lignes, enBloc, texteBrut } = await lirePdf(await fichier.arrayBuffer());
    return { format, feuilles: [{ nom: 'PDF', lignes, enBloc, texteBrut }] };
  }
  if (format === 'csv') {
    // Lu comme texte UTF-8 : un CSV sans marque d'encodage perdrait ses accents.
    return { format, feuilles: enTableur(lireClasseur(await fichier.text(), 'string')) };
  }
  if (['xlsx', 'xls', 'ods'].includes(format)) {
    return { format, feuilles: enTableur(lireClasseur(await fichier.arrayBuffer(), 'array')) };
  }
  throw new Error('Format non pris en charge : choisissez un fichier Excel (.xlsx, .xls), ODS, CSV ou PDF.');
};
